
import { useState } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { StatCard } from "@/components/dashboard/StatCard";
import { CampaignCard } from "@/components/dashboard/CampaignCard";
import { Button } from "@/components/ui/button";
import { GiftIcon, Plus, TicketIcon, TrendingUp, Users } from "lucide-react";
import { mockCampaigns, mockStats } from "@/data/mockData";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState("all");
  
  // Filter campaigns based on active tab
  const filteredCampaigns = mockCampaigns.filter(campaign => {
    if (activeTab === "all") return true;
    return campaign.status === activeTab;
  });

  const activeCampaigns = mockCampaigns.filter(campaign => campaign.status === "active");
  
  return (
    <AdminLayout 
      title="Dashboard" 
      subtitle="Welcome to your BeerDrop Admin Dashboard"
    >
      <div className="space-y-8">
        {/* Stats Overview */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Total Vouchers"
            value={mockStats.totalVouchers}
            icon={TicketIcon}
            trend={{ value: 12, positive: true }}
            description="vs. last month"
          />
          <StatCard
            title="Active Vouchers"
            value={mockStats.activeVouchers}
            icon={GiftIcon}
            trend={{ value: 4, positive: true }}
            description="vs. last month"
          />
          <StatCard
            title="Redeemed Vouchers"
            value={mockStats.redeemedVouchers}
            icon={TrendingUp}
            trend={{ value: 18, positive: true }}
            description="vs. last month"
          />
          <StatCard
            title="Active Campaigns"
            value={mockStats.activeCampaigns}
            icon={Users}
            description={`${activeCampaigns.length} running now`}
          />
        </div>

        {/* Quick Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <h2 className="text-xl font-bold">Promotional Campaigns</h2>
          <Button asChild>
            <Link to="/admin/campaigns/new">
              <Plus className="mr-2 h-4 w-4" /> New Campaign
            </Link>
          </Button>
        </div>

        {/* Campaign Tabs and List */}
        <Tabs 
          defaultValue="all" 
          value={activeTab} 
          onValueChange={setActiveTab}
          className="space-y-4"
        >
          <TabsList>
            <TabsTrigger value="all">All Campaigns</TabsTrigger>
            <TabsTrigger value="active">Active</TabsTrigger>
            <TabsTrigger value="scheduled">Scheduled</TabsTrigger>
            <TabsTrigger value="ended">Ended</TabsTrigger>
            <TabsTrigger value="draft">Drafts</TabsTrigger>
          </TabsList>
          
          <TabsContent value={activeTab} className="space-y-4">
            {filteredCampaigns.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-10">
                  <p className="text-muted-foreground text-center mb-4">
                    No {activeTab === "all" ? "" : activeTab + " "} campaigns found.
                  </p>
                  <Button asChild variant="outline">
                    <Link to="/admin/campaigns/new">
                      <Plus className="mr-2 h-4 w-4" /> Create Campaign
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {filteredCampaigns.map((campaign) => (
                  <CampaignCard
                    key={campaign.id}
                    {...campaign}
                    onClick={() => { /* Handle click */ }}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
